// src/components/latest-results/TestGroup.jsx
import TestRow from "./TestRow";
import DiabeticGlucoseRow from "./DiabeticGlucoseRow";
import ENTTestGroup from "./ENTTestGroup";
import {
  ROUTINE_PANEL_PARAMETER_SET,
  URINE_PANEL_PARAMETER_SET,
} from "../../Utils/urineMapping";
import { DEPENDENT_TESTS } from "../../Utils/TestNames";

const createDependentSet = (name) =>
  new Set(
    (
      DEPENDENT_TESTS.find(
        (group) => (group?.name || "").toLowerCase() === name.toLowerCase()
      )?.ids || []
    ).filter(Boolean)
  );

const COMPLETE_RENAL_PROFILE_SET = createDependentSet("Complete Renal Profile");
const LIPID_PROFILE_SET = createDependentSet("Lipid Profile");
const COMPLETE_LIVER_FUNCTION_SET = createDependentSet(
  "Complete Liver Function Tests"
);
const HEMOGLOBIN_SET = createDependentSet("Hemoglobin");


const KIDNEY_SUB_SECTIONS = [
  {
    label: "Routine Panel",
    match: (detail) =>
      typeof detail?.ParameterID === "string" &&
      ROUTINE_PANEL_PARAMETER_SET.has(detail.ParameterID),
  },
  {
    label: "Complete Renal Profile",
    match: (detail) =>
      typeof detail?.ParameterID === "string" &&
      COMPLETE_RENAL_PROFILE_SET.has(detail.ParameterID),
  },

  
];
const CARDIAC_SUB_SECTIONS = [
  {
    label: "Lipid Profile",
    match: (detail) =>
      typeof detail?.ParameterID === "string" &&
      LIPID_PROFILE_SET.has(detail.ParameterID),
  },
];

const BLOOD_SUB_SECTIONS = [
  {
    label: "Hemoglobin",
    match: (detail) =>
      typeof detail?.ParameterID === "string" &&
      HEMOGLOBIN_SET.has(detail.ParameterID),
  },
  // later you can add more blood panels here if needed
];


const LIVER_SUB_SECTIONS = [
  {
    label: "Complete Liver Function Tests",
    match: (detail) =>
      typeof detail?.ParameterID === "string" &&
      COMPLETE_LIVER_FUNCTION_SET.has(detail.ParameterID),
  },
];

const buildSections = (optionList, sectionDefs, otherLabel = null) => {
  if (!Array.isArray(optionList) || optionList.length === 0) {
    return [];
  }

  if (!sectionDefs.length) {
    return [{ label: otherLabel, rows: optionList }];
  }

  const buckets = sectionDefs.map(({ label }) => ({
    label,
    rows: [],
  }));
  const remainder = [];

  optionList.forEach((detail) => {
    const idx = sectionDefs.findIndex(({ match }) => match(detail));
    if (idx >= 0) {
      buckets[idx].rows.push(detail);
    } else {
      remainder.push(detail);
    }
  });

  const filledBuckets = buckets.filter((bucket) => bucket.rows.length > 0);

  if (!filledBuckets.length) {
    return [{ label: otherLabel, rows: optionList }];
  }

  if (remainder.length) {
    filledBuckets.push({
      label: otherLabel,
      rows: remainder,
    });
  }

  return filledBuckets;
};

export default function TestGroup({
  group,
  results,
  onChange,
  isCalculated,
  renderUnitRange,
  isCbcParam,
  rowLoading,
  onFetchOne,
  // 🔹 cough
  isCoughParam,
  onOpenCough,
  // 🔹 camera
  isCameraParam,
  onOpenCamera,
  isStethoscopeParam,
  onOpenStethoscope,
  getEntImages,
  onRemoveEntImage,
  onClearEntImages,
  entImageLimit,
  // 🔹 CBC props
  hasAnyCBC,
  cbcLoading,
  onFetchAllCBC,
  // 🔹 Routine Panel props
  hasRoutinePanel,
  routinePanelLoading,
  onFetchRoutinePanel,
}) {
  const groupName = typeof group?.name === "string" ? group.name.trim() : "";
  const lower = groupName.toLowerCase();

  // tolerate "Blood Marker" / "Blood Markers"
  const isBloodMarker = ["blood marker", "blood markers"].includes(lower);
  const isKidneyMarker = ["kidney marker", "kidney markers"].includes(lower);
  const isLiverMarker = ["liver marker", "liver markers"].includes(lower);
  const isCardiac = ["cardiac"].includes(lower);  
  const groupHasRoutinePanel = (Array.isArray(group?.options) ? group.options : []).some(
    (opt) => URINE_PANEL_PARAMETER_SET.has(opt?.ParameterID)
  );
  const showRoutineButton = isKidneyMarker && !!hasRoutinePanel && groupHasRoutinePanel;
  const showCbcButton = isBloodMarker && !!hasAnyCBC;
  const isDiabeticGroup = groupName === "Diabetic";

  // Safeguard
  const options = Array.isArray(group?.options) ? group.options : [];

  // For Diabetic: hide the two glucose subtests so they don't render as rows
  const filteredOptions = options;
  const subSections = isKidneyMarker
  ? KIDNEY_SUB_SECTIONS
  : isLiverMarker
  ? LIVER_SUB_SECTIONS
  : isCardiac
  ? CARDIAC_SUB_SECTIONS
  : isBloodMarker
  ? BLOOD_SUB_SECTIONS
  : [];


  const otherLabel = isKidneyMarker
  ? "Other Kidney Tests"
  : isLiverMarker
  ? "Other Liver Tests"
  : isCardiac
  ? "Other Cardiac Tests"
  : isBloodMarker
  ? "Complete Blood Count"
  : null;


  const groupedSections = buildSections(filteredOptions, subSections, otherLabel);

  const resolvedEntLimit = typeof entImageLimit === "number" ? entImageLimit : 5;

  return (
    <div className="space-y-4">
      {/* Group Header Row */}
      <div className="flex items-center justify-between">
        <button className="bg-[#D7D4F8] text-[#845BB3] px-4 py-2 rounded-md font-semibold text-xl">
          {groupName}
        </button>

        {(showRoutineButton || showCbcButton) && (
          <div className="flex items-center gap-3">
            {showRoutineButton && (
              <button
                className="bg-[#845BB3] text-white px-4 py-2 rounded-md font-semibold text-sm hover:opacity-90 transition disabled:opacity-60"
                onClick={onFetchRoutinePanel}
                disabled={routinePanelLoading}
                title="Fetch all Routine Panel (urine strip) results at once"
              >
                {routinePanelLoading ? "Fetching Routine..." : "Fetch Routine Panel"}
              </button>
            )}
            {showCbcButton && (
              <button
                className="bg-[#845BB3] text-white px-4 py-2 rounded-md font-semibold text-sm hover:opacity-90 transition disabled:opacity-60"
                onClick={onFetchAllCBC}
                disabled={cbcLoading}
                title="Fetch all CBC results (BL*) at once"
              >
                {cbcLoading ? "Fetching ALL CBC..." : "Fetch ALL CBC Results"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Test rows */}
      {groupedSections.map(({ label, rows }, sectionIdx) => (
        <div key={label || `section-${sectionIdx}`} className="space-y-2">
          {label && (
            <div className="inline-flex rounded-md bg-[#F1EEFF] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#845BB3]">
              {label}
            </div>
          )}
          <div className="space-y-4">
            {rows.map((detail) => {
              if (detail?.ParameterID === "EN4058") {
                const entImages =
                  typeof getEntImages === "function"
                    ? getEntImages(detail.ParameterID)
                    : Array.isArray(results?.[detail.ParameterID])
                    ? results[detail.ParameterID]
                    : [];

                return (
                  <ENTTestGroup
                    key={detail.ParameterID}
                    groupName={groupName}
                    detail={detail}
                    images={entImages}
                    onAddImage={() => onOpenCamera?.(detail)}
                    onRemoveImage={(idx) =>
                      onRemoveEntImage?.(detail.ParameterID, idx)
                    }
                    onRemoveAll={() =>
                      onClearEntImages?.(detail.ParameterID)
                    }
                    maxImages={resolvedEntLimit}
                    showHeader={false}
                  />
                );
              }

              const isCBC =
                typeof isCbcParam === "function"
                  ? isCbcParam(detail?.ParameterID)
                  : false;

              const isCough =
                typeof isCoughParam === "function" ? isCoughParam(detail) : false;

              const isCamera =
                typeof isCameraParam === "function" ? isCameraParam(detail) : false;

              const isStethoscope =
                typeof isStethoscopeParam === "function"
                  ? isStethoscopeParam(detail)
                  : false;

              // In Diabetic group, replace the main Glucose row with the special selector row
              const isGlucoseMain =
                isDiabeticGroup && detail?.ParameterID === "DI9470";

              if (isGlucoseMain) {
                return (
                  <DiabeticGlucoseRow
                    key={detail.ParameterID}
                    detail={detail}                 // has subTests
                    valuesMap={results}             // use existing results as a map
                    onChange={onChange}
                    isCalculated={isCalculated}
                    renderUnitRange={renderUnitRange}
                    rowLoading={rowLoading}
                    onFetchOne={onFetchOne}
                  />
                );
              }

              // Default row for everything else
              return (
                <TestRow
                  key={detail.ParameterID}
                  detail={detail}
                  value={results?.[detail.ParameterID]}
                  onChange={onChange}
                  isCalculated={isCalculated}
                  renderUnitRange={renderUnitRange}
                  isCBC={isCBC}
                  rowLoading={rowLoading}
                  onFetchOne={onFetchOne}
                  isCough={isCough}
                  onOpenCough={onOpenCough}
                  isCamera={isCamera}
                  onOpenCamera={onOpenCamera}
                  isStethoscope={isStethoscope}
                  onOpenStethoscope={onOpenStethoscope}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
